import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { X, ChevronLeft, Search } from 'lucide-react'
import { LANGUAGES, REGIONS } from '@olimhub/shared'
import type { PostType, Language, CategorizablePostType, GeocodedPoint } from '@olimhub/shared'
import { searchApi, groupsApi, geocodingApi, accountsApi } from '@/api'
import { useCategoryOptions } from '@/hooks'
import { useAuthStore } from '@/store/auth.store'
import { FeedItem } from '@/components/feed/FeedItem'
import { AccountCard } from '@/components/profile/AccountCard'
import { SuggestedAccounts } from '@/components/profile'
import { GroupCard } from '@/components/groups/GroupCard'
import { AddressAutocompleteInput, PostCardSkeleton, AccountCardSkeleton, DateFieldDDMMYYYY } from '@/components/common'
import { SearchResultsMap } from '@/components/search/SearchResultsMap'
import { useSuggestionsStore } from '@/store/suggestions.store'
import { useSearchStore, type LocationMode } from '@/store/search.store'
import { getFeedItemId } from '@/lib/feedItem'

// Search's merged map default center (spec-search-map-merge.md) — the
// viewer's own saved location when they have one (fetched via the self-only
// /accounts/me/location, see accounts.service.ts's getMyLocation), else
// Haifa, roughly central to where OlimHub's community concentrates.
const HAIFA_CENTER: [number, number] = [32.794, 34.9896]

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '8px 10px',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-strong)',
  background: 'var(--surface)',
}

const FACET_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: 4,
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-muted)',
}

// Event/Bazaar/Help/Action all take a Category facet (spec-search-fase2-facetas.md).
const CATEGORIZABLE_TYPES: PostType[] = ['event', 'offer', 'help', 'action']
// Event/Action share the date-range + Group facets.
const DATE_GROUP_FACET_TYPES: PostType[] = ['event', 'action']

const DEBOUNCE_MS = 400

// Single flattened type selector (spec-ajuste-flatten-tipo.md) — 'all' shows
// everything (posts of every type + People) with no facets; each PostType
// value is its own directly-selectable chip (no more "Posts" container
// wrapping Event/Bazaar/Help/Action); 'accounts' is People, 'groups' is
// Groups (spec-ajuste-pills-scroll-groups.md) — a basic keyword search over
// group name/description, no facets of its own yet (Fase 3).
type SelectedType = 'all' | PostType | 'accounts' | 'groups'
const SELECTABLE_TYPES: SelectedType[] = ['post', 'event', 'offer', 'help', 'action', 'groups', 'accounts']

function parseSelectedType(raw: string | null): SelectedType {
  return raw && (SELECTABLE_TYPES as string[]).includes(raw) ? (raw as SelectedType) : 'all'
}

function PillSelector<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div style={{ display: 'inline-flex', padding: 3, gap: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)' }}>
      {options.map((opt) => {
        const on = value === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              background: on ? 'var(--brand)' : 'transparent',
              color: on ? 'var(--on-brand)' : 'var(--text-muted)',
              transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

const EDGE_FADE = 20

// Same scroll + edge-fade technique as FeedTypeChips.tsx (Local/Following
// Nível 2 chips, spec-ajuste-pills-scroll-groups.md wants this bar to match
// it) — single-select instead of multi-select, so it's its own component
// rather than a variant of PillSelector's single-background segmented look.
function TypeChipBar<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (v: T) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Edge fade is driven by actual scroll position (not just "does it
  // overflow") so it disappears once scrolled all the way to that side.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    function update() {
      setCanScrollLeft(el!.scrollLeft > 2)
      setCanScrollRight(el!.scrollLeft + el!.clientWidth < el!.scrollWidth - 2)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [options])

  const maskImage = !canScrollLeft && !canScrollRight
    ? undefined
    : `linear-gradient(to right, ${canScrollLeft ? 'transparent 0, black ' + EDGE_FADE + 'px' : 'black 0'}, black calc(100% - ${canScrollRight ? EDGE_FADE : 0}px), ${canScrollRight ? 'transparent 100%' : 'black 100%'})`

  // overflowX lives on this single element, with no justify-content —
  // combining the two clips the start of an overflowing line with no way to
  // scroll back to it (LESSONS_LEARNED.md §56.4). Padding (esp. bottom)
  // lives on this same scrolling element too, not a wrapper around it —
  // otherwise a rendered native scrollbar sits flush under the chips with
  // no clearance, visually overlapping them.
  return (
    <div
      ref={scrollRef}
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        padding: '6px 12px 12px',
        WebkitMaskImage: maskImage,
        maskImage,
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            style={{
              padding: '5px 12px',
              border: '1.5px solid',
              borderColor: active ? 'var(--brand)' : 'var(--border)',
              borderRadius: 'var(--radius-pill)',
              background: active ? 'var(--brand)' : 'transparent',
              color: active ? 'var(--on-brand)' : 'var(--text-body)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              cursor: 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

function MultiPillSelector<T extends string>({
  options,
  value,
  onToggle,
}: {
  options: Array<{ value: T; label: string }>
  value: T[]
  onToggle: (v: T) => void
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((opt) => {
        const on = value.includes(opt.value)
        return (
          <button
            key={opt.value}
            onClick={() => onToggle(opt.value)}
            style={{
              border: on ? '1.5px solid var(--brand)' : '1.5px solid var(--border)',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              background: on ? 'var(--brand-subtle)' : 'transparent',
              color: on ? 'var(--brand)' : 'var(--text-muted)',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// Sized to sit inline next to a PillSelector row (the Settings page's own
// Toggle is 44×24, built for a standalone preference row).
function SwitchToggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0 }}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(!on)}
        style={{
          width: 32,
          height: 18,
          borderRadius: 'var(--radius-pill)',
          border: 'none',
          background: on ? 'var(--brand)' : 'var(--border-strong)',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background var(--dur-fast) var(--ease-out)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            insetInlineStart: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            transition: 'inset-inline-start var(--dur-fast) var(--ease-out)',
          }}
        />
      </button>
      <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>{label}</span>
    </label>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
      <Search size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
      <p style={{ margin: '0 0 6px', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
        {message}
      </p>
    </div>
  )
}

export function SearchPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { fetchSuggestions } = useSuggestionsStore()

  const initialQ = searchParams.get('q') ?? ''
  const initialType = parseSelectedType(searchParams.get('type'))

  const [inputValue, setInputValue] = useState(initialQ)
  const [query, setQuery] = useState(initialQ)
  const [selectedType, setSelectedType] = useState<SelectedType>(initialType)

  // Every facet below (and the results map's view state) lives in
  // search.store.ts, not component-local useState — see its own comment
  // for why: it needs to survive SearchPage unmounting when the user taps
  // a map pin's "View details" and navigates back.
  const {
    language, setLanguage,
    helpStatus, setHelpStatus,
    categoryId, setCategoryId,
    startsAfter, setStartsAfter,
    startsBefore, setStartsBefore,
    selectedGroup, setSelectedGroup,
    groupQuery, setGroupQuery,
    urgentOnly, setUrgentOnly,
    accountLanguages, setAccountLanguages,
    groupRegion, setGroupRegion,
    groupOpenFilter, setGroupOpenFilter,
    locationMode, setLocationMode,
    radiusKm, setRadiusKm,
    addressText, setAddressText,
    referenceLat, setReferenceLat,
    referenceLng, setReferenceLng,
    mapExpanded, setMapExpanded,
    mapCenter, mapZoom, setMapView,
    resetTypeFacets,
  } = useSearchStore()
  // Ephemeral UI state, deliberately still local — a dropdown should always
  // start closed, no benefit to restoring it on remount.
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false)

  const { account, isAuthenticated } = useAuthStore()
  const hasProfileLocation = account?.hasLocation ?? false
  // 'profile' and 'map_center' share one slot in the mode-pill row instead
  // of both always showing (with 'profile' just greyed out when there's no
  // saved location to use) — whichever is actually usable takes that slot.
  const locationModeOptions: LocationMode[] = hasProfileLocation
    ? ['off', 'profile', 'address']
    : ['off', 'map_center', 'address']

  const myLocationQuery = useQuery({
    queryKey: ['search', 'my-location'],
    queryFn: () => accountsApi.getMyLocation(),
    enabled: isAuthenticated && hasProfileLocation,
    staleTime: 5 * 60_000,
  })
  const myLocation = myLocationQuery.data
  // The map's restored view (if the user already opened/moved it this
  // session) takes priority over recomputing profile-location-or-Haifa.
  const mapInitialCenter: [number, number] = mapCenter ?? (myLocation ? [myLocation.latitude, myLocation.longitude] : HAIFA_CENTER)
  // Reference-point marker (see SearchResultsMap's own comment) — 'off' and
  // 'map_center' don't need one: 'map_center' already shows its own
  // screen-fixed pin. Memoized on the underlying primitives, not just
  // recomputed inline: FitBoundsToPins re-fits whenever this array's
  // *identity* changes (SearchResultsMap.tsx), and dragging the map already
  // triggers a re-render via ViewTracker -> setMapView (search.store.ts) on
  // every moveend — an unmemoized literal here would get a new identity on
  // every one of those renders even though the lat/lng didn't change,
  // re-fitting the bounds and snapping the user's drag back mid-gesture.
  const referencePoint: [number, number] | null = useMemo(() => {
    if (locationMode === 'profile') return myLocation ? [myLocation.latitude, myLocation.longitude] : null
    if (locationMode === 'address') return referenceLat !== null && referenceLng !== null ? [referenceLat, referenceLng] : null
    return null
  }, [locationMode, myLocation, referenceLat, referenceLng])

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (selectedType === 'accounts') void fetchSuggestions()
  }, [selectedType, fetchSuggestions])

  // Choosing a mode that needs the map to actually pick/show a center
  // auto-expands it — 'profile' doesn't (its reference point is already
  // known, no map interaction needed), and the user can still manually
  // collapse/expand at any time afterward.
  useEffect(() => {
    if (locationMode === 'address' || locationMode === 'map_center') setMapExpanded(true)
  }, [locationMode])

  const handleInput = useCallback((v: string) => {
    setInputValue(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery(v)
      const params: Record<string, string> = {}
      if (v) params['q'] = v
      if (selectedType !== 'all') params['type'] = selectedType
      setSearchParams(params, { replace: true })
    }, DEBOUNCE_MS)
  }, [selectedType, setSearchParams])

  // Discards the previous type's facets rather than keeping them
  // applied-but-hidden — see spec-search-fase2-facetas.md /
  // spec-ajuste-flatten-tipo.md edge cases. `language` is a persistent
  // cross-type preference, not a per-type facet, so it's deliberately not
  // reset here (unchanged from the pre-flatten behavior).
  function selectType(v: SelectedType) {
    setSelectedType(v)
    resetTypeFacets()
    setGroupDropdownOpen(false)
    const params: Record<string, string> = {}
    if (query) params['q'] = query
    if (v !== 'all') params['type'] = v
    setSearchParams(params, { replace: true })
  }

  const effectiveQuery = query.trim().length >= 2 ? query.trim() : ''

  const isAll = selectedType === 'all'
  const isAccounts = selectedType === 'accounts'
  const isGroups = selectedType === 'groups'
  const isPostsMode = !isAll && !isAccounts && !isGroups
  const currentPostType = isPostsMode ? (selectedType as PostType) : undefined

  const showCategoryFacet = isPostsMode && CATEGORIZABLE_TYPES.includes(selectedType as PostType)
  const showDateGroupFacets = isPostsMode && DATE_GROUP_FACET_TYPES.includes(selectedType as PostType)
  // Same facet, same state, for all three applicable types — see spec's
  // "não precisa ser reconfigurada a cada troca" edge case.
  const showLocationFacet = showDateGroupFacets || isGroups

  // Only included once a usable reference point actually exists — 'profile'
  // needs none (server resolves it from the JWT); 'address'/'map_center'
  // need referenceLat/Lng resolved first (geocode result / map's initial
  // center), so a facet turned on mid-typing doesn't fire a bad request.
  const locationParams = locationMode !== 'off' && (locationMode === 'profile' || (referenceLat !== null && referenceLng !== null))
    ? {
        locationMode,
        radiusKm,
        referenceLat: locationMode === 'profile' ? undefined : referenceLat ?? undefined,
        referenceLng: locationMode === 'profile' ? undefined : referenceLng ?? undefined,
      }
    : undefined

  // `resolved` is already the coordinates when AddressAutocompleteInput's
  // pick came from Google Places — only falls through to a server geocode
  // call for a Nominatim fallback pick (no placeId to resolve from).
  async function handleAddressSelect(text: string, resolved?: GeocodedPoint) {
    if (resolved) {
      setReferenceLat(resolved.latitude)
      setReferenceLng(resolved.longitude)
      return
    }
    try {
      const point = await geocodingApi.geocode(text)
      setReferenceLat(point.latitude)
      setReferenceLng(point.longitude)
    } catch {
      setReferenceLat(null)
      setReferenceLng(null)
    }
  }

  const { options: categoryOptions, language: categoryLang } = useCategoryOptions(
    (showCategoryFacet ? selectedType : 'event') as CategorizablePostType,
    showCategoryFacet,
  )
  const { options: groupCategoryOptions, language: groupCategoryLang } = useCategoryOptions('group', isGroups)

  const groupPickerQuery = useQuery({
    queryKey: ['search', 'group-picker', groupQuery],
    queryFn: () => groupsApi.list({ q: groupQuery.trim() }),
    enabled: showDateGroupFacets && groupQuery.trim().length >= 2,
    staleTime: 15_000,
  })

  // ─── Posts (used by both "All" and every specific content type) ───────────

  const postsQuery = useQuery({
    queryKey: [
      'search', 'posts', effectiveQuery,
      currentPostType,
      isPostsMode ? language : undefined,
      isPostsMode ? helpStatus : undefined,
      isPostsMode ? categoryId : undefined,
      isPostsMode ? selectedGroup?.slug : undefined,
      isPostsMode ? startsAfter : undefined,
      isPostsMode ? startsBefore : undefined,
      isPostsMode ? urgentOnly : undefined,
      showDateGroupFacets ? locationParams : undefined,
    ],
    queryFn: () => searchApi.searchPosts(effectiveQuery, {
      type: currentPostType,
      language: isPostsMode && language !== 'all' ? language as Language : undefined,
      status: selectedType === 'help' && helpStatus !== 'all' ? helpStatus : undefined,
      category: showCategoryFacet && categoryId ? categoryId : undefined,
      group: showDateGroupFacets && selectedGroup ? selectedGroup.slug : undefined,
      startsAfter: showDateGroupFacets && startsAfter ? startsAfter : undefined,
      startsBefore: showDateGroupFacets && startsBefore ? startsBefore : undefined,
      urgent: selectedType === 'help' && urgentOnly ? true : undefined,
      ...(showDateGroupFacets && locationParams ? locationParams : {}),
    }),
    enabled: !isAccounts && !isGroups,
    staleTime: 30_000,
  })

  // ─── Groups (keyword + Category/Region/Open-Closed/Location facets) ───────
  // Same "browsable by default, keyword narrows it" behavior as every other
  // specific type chip (empty query still runs, showing all groups).

  const groupsQuery = useQuery({
    queryKey: ['search', 'groups', effectiveQuery, categoryId, groupRegion, groupOpenFilter, groupCategoryLang, isGroups ? locationParams : undefined],
    queryFn: () => groupsApi.list({
      q: effectiveQuery || undefined,
      category: categoryId || undefined,
      region: groupRegion !== 'any' ? groupRegion : undefined,
      isOpen: groupOpenFilter === 'all' ? undefined : groupOpenFilter === 'open',
      ...(locationParams ?? {}),
    }, groupCategoryLang),
    enabled: isGroups,
    staleTime: 30_000,
  })

  // ─── Results map (spec-search-map-merge.md) ────────────────────────────────
  // Same facets as postsQuery/groupsQuery above, but unbounded (no
  // MAX_PAGE_SIZE cap) and always excluding past Events/Actions — the whole
  // matching set is meant to render on one map, not a paginated list.
  // Fires whenever the tab holds a geolocatable type, independent of
  // whether a location facet/radius is even active.

  const mapPinsQuery = useQuery({
    queryKey: [
      'search', 'map', selectedType, effectiveQuery, categoryId,
      isGroups ? groupRegion : selectedGroup?.slug,
      isGroups ? groupOpenFilter : [startsAfter, startsBefore],
      locationParams,
    ],
    queryFn: () => isGroups
      ? groupsApi.getMapPins({
          q: effectiveQuery || undefined,
          category: categoryId || undefined,
          region: groupRegion !== 'any' ? groupRegion : undefined,
          isOpen: groupOpenFilter === 'all' ? undefined : groupOpenFilter === 'open',
          ...(locationParams ?? {}),
        })
      : searchApi.getMapPins(selectedType as 'event' | 'action', effectiveQuery, {
          category: categoryId || undefined,
          group: selectedGroup ? selectedGroup.slug : undefined,
          startsAfter: startsAfter || undefined,
          startsBefore: startsBefore || undefined,
          ...(locationParams ?? {}),
        }),
    enabled: showLocationFacet,
    staleTime: 30_000,
  })

  // ─── Accounts strip inside "All" ────────────────────────────────────────
  // excludeGroups: group (mirror) accounts get their own dedicated Search
  // type in a future spec — out of scope here, see spec-ajuste-people.md.

  const accountsPreviewQuery = useQuery({
    queryKey: ['search', 'accounts-preview', effectiveQuery],
    queryFn: () => searchApi.searchAccounts(effectiveQuery, { limit: 5, excludeGroups: true }),
    enabled: isAll && effectiveQuery !== '',
    staleTime: 30_000,
  })

  // ─── People ───────────────────────────────────────────────────────────────

  const showSuggestions = isAccounts && effectiveQuery === '' && accountLanguages.length === 0

  const accountsQuery = useQuery({
    queryKey: ['search', 'accounts', effectiveQuery, accountLanguages],
    queryFn: () => searchApi.searchAccounts(effectiveQuery, {
      languages: accountLanguages.length ? accountLanguages : undefined,
      excludeGroups: true,
    }),
    enabled: isAccounts && !showSuggestions,
    staleTime: 30_000,
  })

  const helpStatusOptions: Array<{ value: 'all' | 'open' | 'resolved'; label: string }> = [
    { value: 'all', label: t('search.status_all') },
    { value: 'open', label: t('search.status_open') },
    { value: 'resolved', label: t('search.status_resolved') },
  ]

  const langOptions: Array<{ value: Language | 'all'; label: string }> = [
    { value: 'all', label: t('events.filter_all') },
    ...LANGUAGES.filter((l) => l !== 'he').map((l) => ({ value: l as Language, label: l.toUpperCase() })),
  ]

  const accountLangOptions = LANGUAGES.filter((l) => l !== 'he').map((l) => ({ value: l as Language, label: l.toUpperCase() }))

  // Single flattened selector (spec-ajuste-flatten-tipo.md, spec-ajuste-
  // pills-scroll-groups.md) — order: All | Posts | Action | Help | Event |
  // Bazaar | Groups | People. The bar scrolls (TypeChipBar) at any screen
  // width now, so People no longer needs to be hidden on mobile.
  const typeOptions: Array<{ value: SelectedType; label: string }> = [
    { value: 'all', label: t('search.type_all_combined') },
    { value: 'post', label: t('search.posts') },
    { value: 'action', label: t('search.type_action') },
    { value: 'help', label: t('search.type_help') },
    { value: 'event', label: t('search.type_event') },
    { value: 'offer', label: t('search.type_bazaar') },
    { value: 'groups', label: t('nav.groups') },
    { value: 'accounts', label: t('search.people') },
  ]

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: '100%', paddingTop: 10 }}>
      {/* Search header — back button + input + type selector */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 'var(--z-header)',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          boxShadow: '0 2px 6px rgb(0 0 0 / 0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px 6px' }}>
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'var(--text-strong)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <ChevronLeft size={22} />
          </button>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--surface-2)',
              border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              padding: '0 12px',
              height: 44,
            }}
          >
            <input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => handleInput(e.target.value)}
              placeholder={t('search.placeholder')}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body)',
                color: 'var(--text-strong)',
                outline: 'none',
              }}
            />
            {inputValue && (
              <button
                onClick={() => {
                  setInputValue('')
                  setQuery('')
                  const params: Record<string, string> = {}
                  if (selectedType !== 'all') params['type'] = selectedType
                  setSearchParams(params, { replace: true })
                }}
                style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <TypeChipBar options={typeOptions} value={selectedType} onChange={selectType} />
      </div>

      {/* Content facets */}
      {isPostsMode && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            marginTop: 10,
            marginInline: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <PillSelector options={langOptions} value={language} onChange={setLanguage} />
          </div>
          {selectedType === 'help' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflowX: 'auto' }}>
              <PillSelector options={helpStatusOptions} value={helpStatus} onChange={setHelpStatus} />
              <SwitchToggle on={urgentOnly} onChange={setUrgentOnly} label={t('search.facet_urgent')} />
            </div>
          )}

          {showCategoryFacet && (
            <div>
              <label style={FACET_LABEL_STYLE}>{t('post.category')}</label>
              <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value || null)} style={INPUT_STYLE}>
                <option value="">{t('events.filter_all')}</option>
                {categoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.translations[categoryLang] ?? cat.translations.en}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showDateGroupFacets && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={FACET_LABEL_STYLE}>{t('events.filter_date_from')}</label>
                <DateFieldDDMMYYYY value={startsAfter} onChange={setStartsAfter} inputStyle={INPUT_STYLE} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={FACET_LABEL_STYLE}>{t('events.filter_date_to')}</label>
                <DateFieldDDMMYYYY value={startsBefore} onChange={setStartsBefore} inputStyle={INPUT_STYLE} />
              </div>
            </div>
          )}

          {showDateGroupFacets && (
            <div style={{ position: 'relative' }}>
              <label style={FACET_LABEL_STYLE}>{t('search.facet_group')}</label>
              {selectedGroup ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1.5px solid var(--brand)', borderRadius: 'var(--radius-pill)', paddingBlock: 5, paddingInlineStart: 12, paddingInlineEnd: 6, background: 'var(--brand-subtle)' }}>
                  <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-xs)', color: 'var(--brand)' }}>{selectedGroup.name}</span>
                  <button
                    onClick={() => setSelectedGroup(null)}
                    aria-label="Clear group filter"
                    style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: 'var(--brand)' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={groupQuery}
                    onChange={(e) => { setGroupQuery(e.target.value); setGroupDropdownOpen(true) }}
                    onFocus={() => setGroupDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setGroupDropdownOpen(false), 150)}
                    placeholder={t('search.facet_group_placeholder')}
                    style={INPUT_STYLE}
                  />
                  {groupDropdownOpen && groupQuery.trim().length >= 2 && (groupPickerQuery.data?.items.length ?? 0) > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        insetInlineStart: 0,
                        insetInlineEnd: 0,
                        zIndex: 5,
                        background: 'var(--surface)',
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-card)',
                        marginTop: 4,
                        overflow: 'hidden',
                      }}
                    >
                      {groupPickerQuery.data!.items.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => { setSelectedGroup({ slug: g.slug, name: g.name }); setGroupQuery(''); setGroupDropdownOpen(false) }}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'start',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid var(--border)',
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-body)',
                          }}
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* People facets */}
      {isAccounts && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            marginTop: 10,
            marginInline: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <MultiPillSelector
              options={accountLangOptions}
              value={accountLanguages}
              onToggle={(l) => setAccountLanguages(accountLanguages.includes(l) ? accountLanguages.filter((x) => x !== l) : [...accountLanguages, l])}
            />
          </div>
        </div>
      )}

      {/* Group facets (Fase 3) */}
      {isGroups && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            marginTop: 10,
            marginInline: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
          }}
        >
          <div>
            <label style={FACET_LABEL_STYLE}>{t('post.category')}</label>
            <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value || null)} style={INPUT_STYLE}>
              <option value="">{t('post.category_none')}</option>
              {groupCategoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.translations[groupCategoryLang] ?? cat.translations.en}
                </option>
              ))}
            </select>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <PillSelector
              options={[
                { value: 'any' as const, label: t('groups.region_none') },
                ...REGIONS.map((r) => ({ value: r, label: t(`groups.region.${r}`) })),
              ]}
              value={groupRegion}
              onChange={setGroupRegion}
            />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <PillSelector
              options={[
                { value: 'all' as const, label: t('search.status_all') },
                { value: 'open' as const, label: t('search.status_open') },
                { value: 'closed' as const, label: t('groups.closed') },
              ]}
              value={groupOpenFilter}
              onChange={setGroupOpenFilter}
            />
          </div>
        </div>
      )}

      {/* Location facet (Event/Action/Groups) — one shared block, state
          persists across those three types (see selectType's comment). */}
      {showLocationFacet && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            marginTop: 10,
            marginInline: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
          }}
        >
          <div>
            <label style={FACET_LABEL_STYLE}>{t('search.location_label')}</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {locationModeOptions.map((mode) => {
                const disabled = mode === 'address' ? !isAuthenticated : false
                const active = locationMode === mode
                const labelKey = mode === 'off' ? 'search.location_off'
                  : mode === 'profile' ? 'search.location_mode_profile'
                  : mode === 'address' ? 'search.location_mode_address'
                  : 'search.location_mode_map'
                return (
                  <button
                    key={mode}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    onClick={() => setLocationMode(mode)}
                    style={{
                      padding: '5px 12px',
                      border: 'none',
                      borderRadius: 'var(--radius-pill)',
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 700,
                      fontSize: 'var(--fs-xs)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.45 : 1,
                      background: active ? 'var(--brand)' : 'transparent',
                      color: active ? 'var(--on-brand)' : 'var(--text-muted)',
                    }}
                  >
                    {t(labelKey)}
                  </button>
                )
              })}
            </div>
            {!isAuthenticated && (
              <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                {t('search.location_signin_hint')}
              </p>
            )}
          </div>

          {locationMode !== 'off' && (
            <>
              <div>
                <label style={FACET_LABEL_STYLE}>{t('search.location_radius_label', { km: radiusKm })}</label>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={10}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              {locationMode === 'address' && (
                <div>
                  <label style={FACET_LABEL_STYLE}>{t('search.location_address_label')}</label>
                  <AddressAutocompleteInput
                    value={addressText}
                    onChange={(v) => { setAddressText(v); setReferenceLat(null); setReferenceLng(null) }}
                    onSelect={(v, resolved) => { void handleAddressSelect(v, resolved) }}
                    placeholder={t('groups.location_placeholder')}
                    style={INPUT_STYLE}
                  />
                </div>
              )}

            </>
          )}

          <div>
            {locationMode === 'map_center' && mapExpanded && (
              <label style={FACET_LABEL_STYLE}>{t('search.location_map_label')}</label>
            )}
            <SearchResultsMap
              pins={mapPinsQuery.data?.pins ?? []}
              expanded={mapExpanded}
              onToggleExpanded={setMapExpanded}
              showCenterPicker={locationMode === 'map_center'}
              onCenterChange={(lat, lng) => { setReferenceLat(lat); setReferenceLng(lng) }}
              initialCenter={mapInitialCenter}
              initialZoom={mapZoom ?? undefined}
              onViewChange={setMapView}
              referencePoint={referencePoint}
            />
          </div>
        </div>
      )}

      {/* Results */}
      <div style={{ flex: 1, paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 8px' }}>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {t('search.results_label')}
          </span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        {isAll && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' }}>
            {(accountsPreviewQuery.data?.items.length ?? 0) > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {accountsPreviewQuery.data!.items.map((account) => (
                  <AccountCard key={account.id} account={account} />
                ))}
              </div>
            )}
            {postsQuery.isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 0 8px' }}>
                <PostCardSkeleton />
                <PostCardSkeleton />
              </div>
            ) : (postsQuery.data?.items.length ?? 0) === 0 ? (
              <EmptyState message={t('search.no_results')} />
            ) : (
              postsQuery.data!.items.map((item, idx) => (
                <FeedItem
                  key={item.kind === 'post' ? `post-${item.data.id}` : `${item.kind}-${getFeedItemId(item)}-${idx}`}
                  item={item}
                  readOnly
                />
              ))
            )}
          </div>
        )}

        {isPostsMode && (
          postsQuery.isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px 8px' }}>
              <PostCardSkeleton />
              <PostCardSkeleton />
              <PostCardSkeleton />
            </div>
          ) : (postsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState message={t('search.no_results')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' }}>
              {postsQuery.data!.items.map((item, idx) => (
                <FeedItem
                  key={item.kind === 'post' ? `post-${item.data.id}` : `${item.kind}-${getFeedItemId(item)}-${idx}`}
                  item={item}
                  readOnly
                />
              ))}
            </div>
          )
        )}

        {isGroups && (
          groupsQuery.isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
              <AccountCardSkeleton />
              <AccountCardSkeleton />
              <AccountCardSkeleton />
            </div>
          ) : (groupsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState message={t('search.no_results')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
              {groupsQuery.data!.items.map((group) => (
                <GroupCard key={group.id} group={group} />
              ))}
            </div>
          )
        )}

        {isAccounts && (
          showSuggestions ? (
            <SuggestedAccounts mode="list" heading={t('suggestions.suggested_accounts')} />
          ) : accountsQuery.isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
              <AccountCardSkeleton />
              <AccountCardSkeleton />
              <AccountCardSkeleton />
            </div>
          ) : (accountsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState message={t('search.no_results')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' }}>
              {accountsQuery.data!.items.map((account) => (
                <AccountCard key={account.id} account={account} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}
